import { useEffect, useState } from "react";
import api, { formatApiErrorDetail, downloadBlob } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { PageHeader, EmptyState, euro, fmtDate } from "@/components/shared";
import { Plus, Building2, Pencil, Trash2, BarChart3, FileDown } from "lucide-react";

const empty = { name: "", vat: "", phone: "", email: "", notes: "" };

export default function Suppliers() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [report, setReport] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);

  const openReport = async (s) => {
    try {
      const { data } = await api.get(`/suppliers/${s.id}/report`);
      setReport(data); setReportOpen(true);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const load = () => api.get("/suppliers").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name) { toast.error("Il nome è obbligatorio"); return; }
    try {
      if (editId) await api.put(`/suppliers/${editId}`, form);
      else await api.post("/suppliers", form);
      toast.success(editId ? "Fornitore aggiornato" : "Fornitore creato");
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Eliminare il fornitore "${s.name}"?`)) return;
    try { await api.delete(`/suppliers/${s.id}`); toast.success("Fornitore eliminato"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader eyebrow="Configurazione" title="Fornitori" subtitle="Anagrafica dei fornitori per le attività e manutenzioni">
        <Button onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} data-testid="new-supplier-button">
          <Plus className="h-4 w-4 mr-1.5" /> Nuovo fornitore
        </Button>
      </PageHeader>

      {items.length ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((s) => (
            <Card key={s.id} data-testid={`supplier-${s.id}`} className="p-5 rounded-lg">
              <div className="flex items-start justify-between">
                <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-primary" />
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => openReport(s)} data-testid={`report-supplier-${s.id}`}>
                    <BarChart3 className="h-4 w-4 text-primary" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => { setForm({ ...empty, ...s }); setEditId(s.id); setOpen(true); }} data-testid={`edit-supplier-${s.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(s)} data-testid={`delete-supplier-${s.id}`}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
              <div className="font-heading font-bold text-lg mt-3">{s.name}</div>
              {s.vat && <p className="text-sm text-muted-foreground mt-1">P.IVA {s.vat}</p>}
              {(s.phone || s.email) && <p className="text-sm text-muted-foreground mt-1">{[s.phone, s.email].filter(Boolean).join(" · ")}</p>}
              <div className="label-caps text-muted-foreground mt-3">{s.usage_count} attività</div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="rounded-lg"><EmptyState icon={Building2} title="Nessun fornitore" subtitle="Aggiungi i fornitori per selezionarli nelle attività." /></Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading text-2xl">{editId ? "Modifica fornitore" : "Nuovo fornitore"}</DialogTitle>
            <DialogDescription>Compila i dati anagrafici del fornitore.</DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4 py-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Ragione sociale *</Label>
              <Input data-testid="supplier-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Es. Officina Rossi S.r.l." />
            </div>
            <div className="space-y-1.5">
              <Label>P.IVA</Label>
              <Input data-testid="supplier-vat-input" value={form.vat} onChange={(e) => setForm({ ...form, vat: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Telefono</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Note</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="save-supplier-button">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="supplier-report-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{report?.supplier?.name}</DialogTitle>
            <DialogDescription>Riepilogo spesa e storico interventi del fornitore.</DialogDescription>
          </DialogHeader>
          {report && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Card className="p-5 rounded-lg">
                  <div className="label-caps text-muted-foreground">Spesa totale</div>
                  <div className="stat-number text-3xl mt-2" data-testid="report-total-spend">{euro(report.total_spend)}</div>
                </Card>
                <Card className="p-5 rounded-lg">
                  <div className="label-caps text-muted-foreground">Interventi</div>
                  <div className="stat-number text-3xl mt-2" data-testid="report-count">{report.count}</div>
                </Card>
              </div>
              <Card className="rounded-lg overflow-hidden">
                {report.records.length ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-secondary/60 hover:bg-secondary/60">
                          <TableHead className="label-caps">Data</TableHead>
                          <TableHead className="label-caps">Bene</TableHead>
                          <TableHead className="label-caps hidden sm:table-cell">Attività</TableHead>
                          <TableHead className="label-caps hidden md:table-cell">N. fattura</TableHead>
                          <TableHead className="label-caps text-right">Importo</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {report.records.map((r) => (
                          <TableRow key={r.id} data-testid={`report-row-${r.id}`}>
                            <TableCell className="text-muted-foreground whitespace-nowrap">{fmtDate(r.date)}</TableCell>
                            <TableCell className="font-semibold">{r.asset_name}</TableCell>
                            <TableCell className="hidden sm:table-cell max-w-xs truncate">{r.activities}</TableCell>
                            <TableCell className="hidden md:table-cell font-mono text-sm">{r.invoice_number || "—"}</TableCell>
                            <TableCell className="text-right tabular-nums font-semibold">{euro(r.invoice_amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <EmptyState icon={Building2} title="Nessun intervento" subtitle="Questo fornitore non ha ancora attività associate." />
                )}
              </Card>
            </div>
          )}
          <DialogFooter>
            {report && (
              <Button variant="outline" onClick={() => downloadBlob(`/suppliers/${report.supplier.id}/report/export`, `report_${report.supplier.name}.xlsx`)} data-testid="export-supplier-report-button" className="font-semibold mr-auto">
                <FileDown className="h-4 w-4 mr-1.5" /> Esporta XLSX
              </Button>
            )}
            <Button variant="outline" onClick={() => setReportOpen(false)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
